import { useMemo, useState } from 'react';
import {
  useAddChecklistItem, useDeleteChecklistItem, useGoKit, usePatchChecklistItem,
  type ChecklistItem,
} from '../api';
import { buildGoKitPdf, goKitFilename } from '../incident/gokit';
import { openBlob, releaseTab, reserveTab } from '../pdf/download';
import { WARD_NAME } from '../plat/document';

/**
 * The go-kit: physical things needed to actually stand up incident command.
 *
 * This exists because the moment you need the list is the moment you are
 * least able to produce it from memory. It defaults open while items remain
 * unchecked and collapses once the kit is together, so it is loud when it
 * matters and out of the way when it does not.
 *
 * The list is the ward's, not an incident's: it is maintained between
 * emergencies and printed to hang in the closet, while the ticks belong to
 * whichever activation is on screen. An item added during a drill is on the
 * list for every response after it, without anybody remembering to carry it.
 */
export default function GoKit({ incidentId, items: given }: {
  /**
   * The activation whose ticks are shown. Absent — the standing list on the
   * incidents page, with nothing active — the list is still editable and
   * printable, but there is nothing to tick against and the boxes say so.
   */
  incidentId?: string;
  /** The board already has the list in its payload; the incidents page does not. */
  items?: ChecklistItem[];
}) {
  const fetched = useGoKit(given ? undefined : incidentId);
  const items = given ?? fetched.data?.items ?? [];
  const patch = usePatchChecklistItem();
  const add = useAddChecklistItem();
  const del = useDeleteChecklistItem();
  const done = items.filter((i) => i.checked).length;
  const [open, setOpen] = useState(done < items.length);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [cat, setCat] = useState('Other');

  const byCategory = useMemo(() => {
    const g = new Map<string, ChecklistItem[]>();
    for (const i of items) {
      const l = g.get(i.category);
      if (l) l.push(i); else g.set(i.category, [i]);
    }
    return [...g.entries()];
  }, [items]);

  if (items.length === 0) return null;
  const allDone = done === items.length;

  const print = async () => {
    // Claimed on the click: see reserveTab.
    const tab = reserveTab();
    try {
      openBlob(await buildGoKitPdf(items, WARD_NAME), goKitFilename(), tab);
    } catch (e) {
      releaseTab(tab);
      throw e;
    }
  };

  return (
    <section className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 print:hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
      >
        <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
          <span className="mr-1 inline-block w-3 text-slate-400">{open ? '▾' : '▸'}</span>
          Go-kit
          {!allDone && (
            <span className="ml-2 rounded bg-amber-100 dark:bg-amber-900/50 px-1.5 py-0.5 text-xs text-amber-800 dark:text-amber-300">
              {items.length - done} to gather
            </span>
          )}
        </span>
        <span className={`text-xs tabular-nums ${allDone ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-500 dark:text-slate-400'}`}>
          {done}/{items.length}
        </span>
      </button>

      {open && (
        <div className="space-y-2 border-t border-slate-200 dark:border-slate-700 px-3 py-2">
          {byCategory.map(([category, group]) => (
            <div key={category}>
              <h4 className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{category}</h4>
              <ul className="mt-0.5 space-y-0.5">
                {group.map((i) => (
                  <li key={i.id} className="group flex items-start gap-1.5 text-sm">
                    <input
                      type="checkbox"
                      checked={i.checked}
                      disabled={!incidentId}
                      title={incidentId ? undefined
                        : 'Ticks belong to an activation — open an incident to pack the kit'}
                      onChange={() => patch.mutate({ incidentId, id: i.id, body: { checked: !i.checked } })}
                      className="mt-1"
                    />
                    <span className={i.checked ? 'text-slate-400 line-through dark:text-slate-600' : 'text-slate-800 dark:text-slate-200'}>
                      {i.item}
                      {i.detail && (
                        <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">— {i.detail}</span>
                      )}
                    </span>
                    <button
                      onClick={() => del.mutate({ incidentId, id: i.id })}
                      className="ml-auto shrink-0 text-xs text-slate-300 opacity-0 group-hover:opacity-100 hover:text-rose-600 dark:text-slate-600"
                      title="Take this off the standing list. Ticks it already carries stay."
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {adding ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!text.trim()) return;
                add.mutate({ incidentId, item: text.trim(), category: cat.trim() || 'Other' },
                  { onSuccess: () => { setText(''); setAdding(false); } });
              }}
              className="flex gap-1"
            >
              <input
                autoFocus value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Something you wished you'd had"
                className="flex-1 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
              />
              <input
                value={cat} onChange={(e) => setCat(e.target.value)}
                className="w-24 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
              />
              <button type="submit" disabled={add.isPending}
                className="rounded bg-slate-800 dark:bg-slate-200 px-2 text-xs text-white dark:text-slate-900">Add</button>
              <button type="button" onClick={() => setAdding(false)} className="text-xs text-slate-400">✕</button>
            </form>
          ) : (
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAdding(true)}
                className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                title="Added items stay on the standing list, for every incident after this one"
              >
                + add an item
              </button>
              <button
                onClick={print}
                className="text-xs text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
                title="One page, unticked, to tick with a pen"
              >
                print the list
              </button>
            </div>
          )}
          {add.error && (
            <p className="text-xs text-rose-600 dark:text-rose-400">{(add.error as Error).message}</p>
          )}
        </div>
      )}
    </section>
  );
}
